import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import type NotebookService from '../../services/NotebookService';
import { formatBytes } from '../../utils/formatters';

export interface VariableInfo {
  name: string;
  type: string;
  value: string;
  shape?: string;
  size?: number;
  len?: number;
  category: string;
  preview?: string;
}

interface VariableInspectorProps {
  notebookService: NotebookService | null;
  /** Incrementing counter that triggers a refresh (e.g., after cell execution) */
  refreshTrigger: number;
}

// Category display config
const CATEGORIES: Record<string, { label: string; color: string; badgeColor: string; icon: string }> = {
  dataframe: {
    label: 'DataFrames & Series',
    color: 'text-orange-400',
    badgeColor: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    icon: 'M3 10h18M3 14h18M3 18h18M3 6h18',
  },
  array: {
    label: 'Arrays & Tensors',
    color: 'text-cyan-400',
    badgeColor: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
    icon: 'M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zm10 0a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zm10 0a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z',
  },
  collection: {
    label: 'Collections',
    color: 'text-purple-400',
    badgeColor: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    icon: 'M4 6h16M4 10h16M4 14h16M4 18h16',
  },
  scalar: {
    label: 'Scalars',
    color: 'text-blue-400',
    badgeColor: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    icon: 'M7 20l4-16m2 16l4-16M6 9h14M4 15h14',
  },
  function: {
    label: 'Functions',
    color: 'text-amber-400',
    badgeColor: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    icon: 'M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4',
  },
  other: {
    label: 'Other',
    color: 'text-gray-400',
    badgeColor: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
    icon: 'M8 12h.01M12 12h.01M16 12h.01',
  },
};

const TYPE_ABBREV: Record<string, string> = {
  DataFrame: 'DF',
  GeoDataFrame: 'GDF',
  Series: 'Ser',
  ndarray: 'np',
  Tensor: 'T',
  tensor: 'T',
  DeviceArray: 'DA',
  list: 'list',
  dict: 'dict',
  tuple: 'tup',
  set: 'set',
  frozenset: 'fset',
  deque: 'deq',
  int: 'int',
  float: 'flt',
  complex: 'cpx',
  str: 'str',
  bool: 'bool',
  NoneType: 'None',
  function: 'fn',
  module: 'mod',
  type: 'cls',
};

type SortField = 'name' | 'type' | 'size';
type SortDir = 'asc' | 'desc';

const CATEGORY_ORDER = ['dataframe', 'array', 'collection', 'scalar', 'function', 'other'];

export const VariableInspector: React.FC<VariableInspectorProps> = ({
  notebookService,
  refreshTrigger,
}) => {
  const [variables, setVariables] = useState<VariableInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('');
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [expandedVar, setExpandedVar] = useState<string | null>(null);
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(new Set());
  const lastFetchRef = useRef(0);

  const fetchVariables = useCallback(async () => {
    if (!notebookService) return;
    const now = Date.now();
    if (now - lastFetchRef.current < 500) return;
    lastFetchRef.current = now;

    setLoading(true);
    try {
      const vars = await notebookService.getVariables();
      setVariables(vars);
    } catch {
      // Kernel might be busy or restarting
    } finally {
      setLoading(false);
    }
  }, [notebookService]);

  useEffect(() => {
    if (refreshTrigger > 0) {
      const id = setTimeout(fetchVariables, 300);
      return () => clearTimeout(id);
    }
  }, [refreshTrigger, fetchVariables]);

  // Fetch once when service becomes available (e.g., reconnecting to existing kernel)
  useEffect(() => {
    if (notebookService) {
      const id = setTimeout(fetchVariables, 500);
      return () => clearTimeout(id);
    }
  }, [notebookService, fetchVariables]);

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const toggleCategory = (cat: string) => {
    setCollapsedCategories(prev => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  };

  const filtered = variables.filter(v =>
    !filter || v.name.toLowerCase().includes(filter.toLowerCase()) ||
    v.type.toLowerCase().includes(filter.toLowerCase())
  );

  const sorted = [...filtered].sort((a, b) => {
    const dir = sortDir === 'asc' ? 1 : -1;
    switch (sortField) {
      case 'name': return dir * a.name.localeCompare(b.name);
      case 'type': return dir * a.type.localeCompare(b.type);
      case 'size': return dir * ((a.size ?? 0) - (b.size ?? 0));
      default: return 0;
    }
  });

  const grouped = useMemo(() => {
    const groups: Record<string, VariableInfo[]> = {};
    for (const v of sorted) {
      const cat = v.category || 'other';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(v);
    }
    return groups;
  }, [sorted]);

  const SortIcon: React.FC<{ field: SortField }> = ({ field }) => {
    if (sortField !== field) return null;
    return (
      <span className="ml-1 text-[10px]">
        {sortDir === 'asc' ? '▲' : '▼'}
      </span>
    );
  };

  return (
    <div className="flex flex-col h-full text-sm">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-800/50">
        <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">
          Variables
          {variables.length > 0 && (
            <span className="ml-1.5 text-gray-500">({variables.length})</span>
          )}
        </span>
        <button
          onClick={fetchVariables}
          disabled={loading || !notebookService}
          className="p-1 text-gray-500 hover:text-white disabled:opacity-30 transition-colors"
          title="Refresh variables"
        >
          <svg className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </button>
      </div>

      {/* Filter + Sort */}
      <div className="px-3 py-1.5 border-b border-gray-800/30 space-y-1.5">
        <input
          type="text"
          value={filter}
          onChange={e => setFilter(e.target.value)}
          placeholder="Filter variables..."
          className="w-full bg-black/30 border border-gray-700/50 rounded px-2 py-1 text-xs text-white placeholder-gray-600 focus:outline-none focus:border-blue-600/50"
        />
        <div className="flex items-center gap-2 text-[10px] text-gray-500">
          <span>Sort:</span>
          {(['name', 'type', 'size'] as SortField[]).map(field => (
            <button
              key={field}
              onClick={() => toggleSort(field)}
              className={`hover:text-gray-300 select-none ${sortField === field ? 'text-gray-300' : ''}`}
            >
              {field === 'size' ? 'mem' : field}<SortIcon field={field} />
            </button>
          ))}
        </div>
      </div>

      {/* Variable groups */}
      <div className="flex-1 overflow-auto">
        {sorted.length === 0 ? (
          <div className="px-3 py-6 text-center text-xs text-gray-600">
            {variables.length === 0
              ? notebookService
                ? 'No variables defined. Run a cell to get started.'
                : 'No kernel connected.'
              : 'No matching variables.'
            }
          </div>
        ) : (
          <div className="py-1">
            {CATEGORY_ORDER.filter(cat => grouped[cat]?.length).map(cat => {
              const config = CATEGORIES[cat] || CATEGORIES.other;
              const vars = grouped[cat];
              const isCollapsed = collapsedCategories.has(cat);

              return (
                <div key={cat} className="mb-0.5">
                  {/* Category header */}
                  <button
                    onClick={() => toggleCategory(cat)}
                    className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-gray-800/30 transition-colors"
                  >
                    <svg className={`w-3 h-3 text-gray-500 transition-transform ${isCollapsed ? '' : 'rotate-90'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    <svg className={`w-3.5 h-3.5 ${config.color}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={config.icon} />
                    </svg>
                    <span className={`text-[10px] font-medium uppercase tracking-wider ${config.color}`}>
                      {config.label}
                    </span>
                    <span className="text-[10px] text-gray-600 ml-auto">{vars.length}</span>
                  </button>

                  {/* Variables in this category */}
                  {!isCollapsed && (
                    <div className="pl-2">
                      {vars.map(v => (
                        <React.Fragment key={v.name}>
                          <button
                            className={`w-full flex items-center gap-2 px-2 py-1.5 text-left transition-colors ${
                              expandedVar === v.name
                                ? 'bg-gray-800/40'
                                : 'hover:bg-gray-800/20'
                            }`}
                            onClick={() => setExpandedVar(expandedVar === v.name ? null : v.name)}
                          >
                            {/* Type badge */}
                            <span className={`inline-flex items-center justify-center px-1.5 py-0.5 text-[9px] font-mono font-medium rounded border ${config.badgeColor} min-w-[28px]`}>
                              {TYPE_ABBREV[v.type] || v.type.slice(0, 3)}
                            </span>

                            {/* Name + shape */}
                            <span className="flex-1 min-w-0 flex items-center gap-1.5">
                              <span className="font-mono text-xs text-white truncate" title={v.name}>
                                {v.name}
                              </span>
                              {v.shape && (
                                <span className="shrink-0 px-1.5 py-0.5 text-[9px] font-mono text-gray-400 bg-gray-800/60 rounded">
                                  {v.shape}
                                </span>
                              )}
                            </span>

                            {/* Memory size */}
                            <span className="text-[10px] text-gray-600 font-mono shrink-0">
                              {v.size !== undefined ? formatBytes(v.size) : ''}
                            </span>
                          </button>

                          {/* Expanded detail */}
                          {expandedVar === v.name && (
                            <div className="mx-2 mb-1 bg-gray-900/60 border border-gray-800/30 rounded">
                              {/* Meta row */}
                              <div className="flex items-center gap-3 px-3 py-1.5 text-[10px] text-gray-500 border-b border-gray-800/20">
                                <span>type: <span className="text-gray-400">{v.type}</span></span>
                                {v.len !== undefined && (
                                  <span>len: <span className="text-gray-400">{v.len.toLocaleString()}</span></span>
                                )}
                                {v.size !== undefined && (
                                  <span>mem: <span className="text-gray-400">{formatBytes(v.size)}</span></span>
                                )}
                              </div>
                              {/* Preview or value */}
                              <pre className="px-3 py-2 text-[11px] text-gray-300 font-mono whitespace-pre-wrap break-all max-h-40 overflow-auto">
                                {v.preview || v.value}
                              </pre>
                            </div>
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
